import React from 'react';
import {Reservation, User} from '../dashboard/types';
import { getStatusBadge } from '../ui/ReservationStatusBadge';

interface ReservationDetailModalProps {
  selectedReservation: Reservation | null;
  reservationDrafts: Record<string, Partial<Reservation>>;
  updateReservationDraft: (
    id: string,
    key: keyof Reservation,
    value: string,
  ) => void;
  onClose: () => void;
  onSave: (id: string) => Promise<boolean>;
}

export const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
  selectedReservation,
  reservationDrafts,
  updateReservationDraft,
  onClose,
  onSave,
}) => {
  if (!selectedReservation) {
    return null;
  }

  return (
    <div
      className='reservation-modal-overlay'
      onClick={onClose}>
      <div
        className='reservation-modal'
        role='dialog'
        aria-modal='true'
        aria-label='Reservation details'
        onClick={(event) => event.stopPropagation()}>
        <div className='reservation-modal-header'>
          <h3>Reservation Details</h3>
          <button
            type='button'
            className='reservation-close'
            onClick={onClose}>
            Close
          </button>
        </div>

        <div className='bg-blue-50 border border-blue-100 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
          <div className='ms-2'>
            <p>
              <strong>Customer:</strong> {selectedReservation.customerName}
            </p>
            <p>
              <strong>Email:</strong> {selectedReservation.customerEmail}
            </p>
            <p>
              <strong>Phone:</strong>{' '}
              {selectedReservation.customerPhone || 'Not provided'}
            </p>
            <p>
              <strong>Status:</strong>{' '}
              {getStatusBadge(selectedReservation.status)}
            </p>
          </div>
          {selectedReservation.table && (
            <div className='ms-2'>
              <p>Table Details</p>
              <p>
                <strong>Table No: </strong>{' '}
                <span>{selectedReservation.table?.tableNumber}</span>
              </p>
              <p>
                <strong>VIP:</strong>{' '}
                <span
                  className={`px-2 text-sm rounded-lg ${selectedReservation.table?.isVip ? ' bg-yellow-500 text-black' : 'bg-gray-200 text-gray-500'}`}>
                  {selectedReservation.table?.isVip ? 'Yes' : 'No'}
                </span>
              </p>
              <p>
                <strong>Capacity:</strong>{' '}
                <span className={``}>
                  {selectedReservation.table?.capacity} seats
                </span>
              </p>
            </div>
          )}
        </div>

        <div className='reservation-form-grid'>
          <label htmlFor='detail-date'>Date</label>
          <input
            id='detail-date'
            type='date'
            value={
              (reservationDrafts[selectedReservation.id]?.date as string) ||
              selectedReservation.date
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'date',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-time'>Time</label>
          <input
            id='detail-time'
            type='time'
            value={
              (reservationDrafts[selectedReservation.id]
                ?.timeStart as string) || selectedReservation.timeStart
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'timeStart',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-time-end'>Time End</label>
          <input
            id='detail-time-end'
            type='time'
            value={
              (reservationDrafts[selectedReservation.id]
                ?.timeEnd as string) || selectedReservation.timeEnd
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'timeEnd',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-guests'>Guests</label>
          <input
            id='detail-guests'
            type='number'
            min={1}
            value={String(
              (reservationDrafts[selectedReservation.id]
                ?.numberOfGuests as number) ||
                selectedReservation.numberOfGuests,
            )}
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'numberOfGuests',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-status'>Status</label>
          <select
            id='detail-status'
            value={
              (reservationDrafts[selectedReservation.id]?.status as string) ||
              selectedReservation.status
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'status',
                e.target.value,
              )
            }>
            <option value='booked'>Booked</option>
            <option value='confirmed'>Confirmed</option>
            <option value='cancelled'>Cancelled</option>
            <option value='paid'>Paid</option>
            <option value='completed'>Completed</option>
          </select>

          {/* Show paid amount field if status is paid_partially or paid_fully */}
          {['paid_partially', 'paid'].includes(
            (reservationDrafts[selectedReservation.id]?.status as string) || selectedReservation.status
          ) && (
            <>
              <label htmlFor='detail-paid-amount'>Paid Amount</label>
              <input
                id='detail-paid-amount'
                type='number'
                min={0}
                value={
                  (reservationDrafts[selectedReservation.id]?.paidAmount as number) ??
                  selectedReservation.paidAmount ?? ''
                }
                onChange={(e) =>
                  updateReservationDraft(
                    selectedReservation.id,
                    'paidAmount',
                    e.target.value,
                  )
                }
              />
            </>
          )}

          <label htmlFor='detail-notes'>Notes</label>
          <textarea
            id='detail-notes'
            className='reservation-notes'
            value={
              (reservationDrafts[selectedReservation.id]?.notes as string) ||
              selectedReservation.notes ||
              ''
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'notes',
                e.target.value,
              )
            }
          />
        </div>

        <div className='w-full flex justify-between gap-2 mt-6 '>
          <button
            type='button'
            className='bg-blue-400 text-white cursor-pointer rounded-lg'
            onClick={async () => {
              const saved = await onSave(selectedReservation.id);
              if (saved) {
                onClose();
              }
            }}>
            Save Changes
          </button>
          <button
            type='button'
            className='bg-gray-200 text-gray-700 px-4 py-2 rounded-lg cursor-pointer'
            onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

interface UserDetailModalProps {
  selectedUser: User | null;
  onClose: () => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  selectedUser,
  onClose,
}) => {
  if (!selectedUser) {
    return null;
  }

  return (
    <div
      className='user-modal-overlay'
      onClick={onClose}>
      <div
        className='user-modal'
        role='dialog'
        aria-modal='true'
        aria-label='User details'
        onClick={(event) => event.stopPropagation()}>
        <div className='reservation-modal-header'>
          <h3>User Details</h3>
          <button
            type='button'
            className='reservation-close'
            onClick={onClose}>
            Close
          </button>
        </div>

        <div className='user-details-grid'>
          <div className='user-detail-item'>
            <p className='profile-label'>Name</p>
            <p>{selectedUser.name}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Email</p>
            <p>{selectedUser.email}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Role</p>
            <p>{selectedUser.role}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Status</p>
            <p>{selectedUser.isActive ? 'Active' : 'Inactive'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Deleted</p>
            <p>{selectedUser.isDeleted ? 'Yes' : 'No'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Last Login</p>
            <p>
              {selectedUser.lastLogin
                ? new Date(selectedUser.lastLogin).toLocaleString()
                : 'Not available'}
            </p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Last Login IP</p>
            <p>{selectedUser.lastLoginIp || 'Not available'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Created At</p>
            <p>{new Date(selectedUser.createdAt).toLocaleString()}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Updated At</p>
            <p>{new Date(selectedUser.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
